/**
 * Playbooks QA round 4 (Unit U4, R-E2 / Key Technical Decision 1 — Spencer's
 * Decision 5, 2026-09-13: never strip or modify Gong step HTML; render it
 * rich instead so it round-trips to Gong byte-for-byte).
 *
 * `containsRealHtmlTags` decides whether a string contains a REAL HTML tag
 * from a fixed whitelist, as opposed to a bracket-style placeholder token
 * (`Call <Acme> then follow up`) or a stray comparison (`price < 5 and > 2`).
 * It is deliberately loose: it looks for the literal substring `<tagname` or
 * `</tagname` immediately followed by whitespace, `/`, or `>`, for a name in
 * the whitelist below. It does NOT validate that the tag (or the body as a
 * whole) is well-formed — that stricter, structural check is
 * `isGongHtmlEnvelope`'s job (`gong-flow-picker-api.ts`), which this function
 * does not replace or widen. A body containing even one real whitelisted tag
 * — even alongside otherwise-malformed surrounding markup — is treated as
 * real HTML: it is genuine Gong-authored markup the rich editor should own,
 * not a placeholder a rep typed.
 *
 * Consumers:
 *  - `gongStepBodyFormat` (`gong-flow-picker-api.ts`) — promotes a non-email
 *    Gong step to `'rich'` when this OR `isGongHtmlEnvelope` matches.
 *  - `stepBlock` (`copy-generation.ts`) — tags the AI prompt's per-step
 *    `(html)`/`(plain)` marker so it matches the body's real render format.
 *
 * Split into its own module (rather than living in `gong-flow-picker-api.ts`,
 * where the whitelist naturally started) because `gong-flow-picker-api.ts`
 * transitively imports this app's browser mutation/api-client layer
 * (`./api-client`, `./mutations`) — importing it from `copy-generation.ts`
 * (which the SERVER's `api.ts` imports) would drag that client-only code
 * into the server bundle. This file has zero imports and is safe from both
 * sides.
 *
 * This module now exports TWO tag vocabularies. This one — the narrow
 * whitelist below — is for BODY CLASSIFICATION and belongs to the two
 * consumers named above; it is deliberately frozen. The broader
 * `EMAIL_HTML_TAG_NAMES` / `isEmailHtmlTagName` / `emailHtmlTagSpacing` set
 * further down is for `sequence-token-flags.ts`'s PLACEHOLDER SCAN, where the
 * narrow list was reporting `<table>`/`<tbody>`/`<tr>`/`<td>` as rep
 * placeholders. Different question, different set, one file.
 *
 * Whitelist: block-level and common inline elements Gong's editor actually
 * emits (`div|p|ul|ol|li|br|strong|em|b|i|a|span|u|h1..h6|blockquote`) —
 * chosen because none of them plausibly collides with a bracket-style
 * placeholder token in the wild (`<Acme>`, `<bold>`, `<pizza>`), and the
 * boundary lookahead (`[\s/>]`) rejects a name that only starts with a
 * whitelisted word (`<bold>`, `<br0>`).
 */
const REAL_HTML_TAG_RE = /<\/?(?:div|p|ul|ol|li|br|strong|em|b|i|a|span|u|h[1-6]|blockquote)(?=[\s/>])/i;

export function containsRealHtmlTags(body: string): boolean {
  return REAL_HTML_TAG_RE.test(body);
}

// The SECOND, BROADER tag set — for the placeholder scan only (C2,
// playbooks-widen-copy-defect-detection).
//
// `containsRealHtmlTags`'s whitelist above answers "should the RICH editor own
// this body?" and has two consumers that depend on its exact current shape
// (`gongStepBodyFormat` promotes a non-email Gong step to `'rich'`;
// `stepBlock` tags the AI prompt `(html)`/`(plain)`). Widening it would change
// BODY CLASSIFICATION, which is a different product decision — so it is
// deliberately left alone.
//
// The placeholder scan asks a different question: "is `<tbody>` a tag, or did
// a rep type a placeholder?" For that, the whitelist is too NARROW — a
// perfectly ordinary Gong/Outlook table body (`<table><tbody><tr><td>`) read
// as four `angle_bracket` placeholders and, before C1/C2, told the rep to fix
// copy that was already correct. `EMAIL_HTML_TAG_NAMES` is the broader answer:
// every tag name that actually shows up inside an email body.
//
// DELIBERATELY EXCLUDED (see {@link EXCLUDED_PLACEHOLDER_TAG_NAMES}): tag
// names that are legal HTML but never appear in email-body markup AND read as
// a plausible rep placeholder. `<Title>` is a job title, `<time>` a meeting
// time, `<address>` a shipping address — flagging those is the whole point of
// the angle-bracket class, so they must NOT be in the set.

/**
 * Tags whose boundary is a visual LINE break in the rendered email — the
 * projection turns each into `\n` so the `^`-anchored scaffolding scan sees
 * one visual line per line (`<p>Opener: …</p>` must read as line-leading).
 */
const EMAIL_BLOCK_TAG_NAMES = [
  'div', 'p', 'br', 'ul', 'ol', 'li', 'blockquote', 'pre',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'caption', 'colgroup', 'col',
  'hr', 'section', 'article', 'header', 'footer', 'center',
  'html', 'head', 'body', 'style', 'script', 'meta', 'link',
] as const;

/**
 * Table CELL tags. A cell boundary separates values on the SAME visual line,
 * so the projection turns these into `\t` rather than `\n` — otherwise every
 * cell of a one-row table would read as its own line and a cell whose text
 * starts with `Ask:` would flag as scaffolding.
 */
const EMAIL_CELL_TAG_NAMES = ['td', 'th'] as const;

/** Inline tags — no visual break at all; the projection drops them outright. */
const EMAIL_INLINE_TAG_NAMES = [
  'strong', 'em', 'b', 'i', 'a', 'span', 'u', 'img',
  'sup', 'sub', 'small', 'code', 'font', 's', 'strike', 'del', 'ins',
  'tt', 'big', 'nobr',
] as const;

/**
 * Tag-shaped words this set deliberately OMITS, so the angle-bracket
 * placeholder scan still flags them. Each is legal HTML that no email body
 * contains, and each reads as a rep's "fill this in" placeholder:
 * `<Title>` (a job title), `<time>` (a meeting time), `<label>`,
 * `<address>`, `<summary>`, `<main>`, `<nav>`, `<aside>`, `<button>`,
 * `<input>`, `<form>`, `<output>`, `<data>`, `<var>`.
 *
 * Exported as the executable form of that reasoning: the agreement test
 * asserts every name here is absent from {@link EMAIL_HTML_TAG_NAMES}, so a
 * later widening cannot silently blind the scan.
 */
export const EXCLUDED_PLACEHOLDER_TAG_NAMES: readonly string[] = [
  'title', 'time', 'label', 'address', 'summary', 'main', 'nav', 'aside',
  'button', 'input', 'form', 'output', 'data', 'var',
];

/** Every tag name that really appears in an email body — the placeholder scan's (broader) known set. */
export const EMAIL_HTML_TAG_NAMES: ReadonlySet<string> = new Set<string>([
  ...EMAIL_BLOCK_TAG_NAMES,
  ...EMAIL_CELL_TAG_NAMES,
  ...EMAIL_INLINE_TAG_NAMES,
]);

/** Whether `name` is a tag name real email HTML uses — case-insensitive, so `<TABLE>` and `<Table>` both count. */
export function isEmailHtmlTagName(name: string): boolean {
  return EMAIL_HTML_TAG_NAMES.has(name.toLowerCase());
}

/**
 * The whitespace a known email tag contributes to a TEXT PROJECTION of the
 * body: `'\n'` for a line-breaking block tag, `'\t'` for a table cell, `''`
 * for an inline tag, and `null` for a name that is NOT known email HTML —
 * `null` means "leave the match alone", which is what keeps `<Company>` in
 * the projection for the angle-bracket scan to find.
 */
export function emailHtmlTagSpacing(name: string): string | null {
  const lower = name.toLowerCase();
  if (!EMAIL_HTML_TAG_NAMES.has(lower)) return null;
  if ((EMAIL_CELL_TAG_NAMES as readonly string[]).includes(lower)) return '\t';
  if ((EMAIL_BLOCK_TAG_NAMES as readonly string[]).includes(lower)) return '\n';
  return '';
}
